<template xmlns="http://www.w3.org/1999/html">
    <div>
        <h2>{{ step.title }}</h2>
        <p class="step__notice">
            All fields with an asterisk are required
            <i class="fas fa-asterisk"></i>
        </p>

        <InputRadioGroup
            name="have_children"
            :required="checkIsRequired('have_children')"
            :error="formRef.errors.get('have_children')"
            v-model="formRef.have_children"
            :options="getField('have_children').options"
            @input="formRef.errors.clear('have_children')"
        >
            <template v-slot:question>
                {{ getField("have_children").question }}
            </template>
        </InputRadioGroup>

        <InputNumber
            v-if="formRef.have_children === 'yes'"
            name="children_count"
            :required="checkIsRequired('children_count')"
            :error="formRef.errors.get('children_count')"
            v-model="formRef.children_count"
        >
            <template v-slot:question>
                {{ getField("children_count").question }}
            </template>
        </InputNumber>

        <InputRadioGroup
            v-if="formRef.have_children === 'yes'"
            name="children_under_two"
            :required="checkIsRequired('children_under_two')"
            :error="formRef.errors.get('children_under_two')"
            v-model="formRef.children_under_two"
            :options="getField('children_under_two').options"
            @input="formRef.errors.clear('children_under_two')"
        >
            <template v-slot:question>
                {{ getField("children_under_two").question }}
            </template>
        </InputRadioGroup>

        <InputRadioGroup
            v-if="formRef.have_children === 'yes'"
            name="only_parent_carer"
            :required="checkIsRequired('only_parent_carer')"
            :error="formRef.errors.get('only_parent_carer')"
            v-model="formRef.only_parent_carer"
            :options="getField('only_parent_carer').options"
            @input="formRef.errors.clear('only_parent_carer')"
        >
            <template v-slot:question>
                {{ getField("only_parent_carer").question }}
            </template>
            <template v-slot:description>
                {{ getField("only_parent_carer").description }}
            </template>
        </InputRadioGroup>

        <InputRadioGroup
            name="living_arrangements"
            :required="checkIsRequired('living_arrangements')"
            :error="formRef.errors.get('living_arrangements')"
            v-model="formRef.living_arrangements"
            v-model:other-text="formRef.living_arrangements_other"
            :options="getField('living_arrangements')['options']"
            @input="handleLivingArrangementInput"
            :form="formRef"
        >
            <template v-slot:question>
                {{ getField("living_arrangements").question }}
            </template>
        </InputRadioGroup>

        <InputRadioGroup
            v-if="!(['Children\'s Home', 'Foster Care'].includes(formRef.living_arrangements))"
            name="in_care_situation"
            :required="true"
            :error="formRef.errors.get('in_care_situation')"
            v-model="formRef.in_care_situation"
            :options="getField('in_care_situation').options"
            @input="formRef.errors.clear('in_care_situation')"
            :form="formRef"
        >
            <template v-slot:question>
                {{ getField("in_care_situation").question }}
            </template>
        </InputRadioGroup>

        <InputRadioGroup
            v-if="['Yes, I’m in care now', 'Yes, but I’ve left care'].includes(formRef.in_care_situation)"
            name="in_care_type"
            :required="true"
            :error="formRef.errors.get('in_care_type')"
            v-model="formRef.in_care_type"
            :options="getField('in_care_type').options"
            @input="formRef.errors.clear('in_care_type')"
            :form="formRef"
        >
            <template v-slot:question>
                {{ getField("in_care_type").question }}
            </template>
        </InputRadioGroup>

        <InputRadioGroup
            name="how_satisfied_with_life"
            :required="checkIsRequired('how_satisfied_with_life')"
            :error="formRef.errors.get('how_satisfied_with_life')"
            v-model="formRef.how_satisfied_with_life"
            :options="getField('how_satisfied_with_life').options"
            @input="formRef.errors.clear('how_satisfied_with_life')"
            :form="formRef"
        >
            <template v-slot:question>
                {{ getField("how_satisfied_with_life").question }}
            </template>
        </InputRadioGroup>

        <InputCheckboxGroup
            name="affected_by"
            :required="checkIsRequired('affected_by')"
            :error="formRef.errors.get('affected_by')"
            v-model="formRef.affected_by"
            :form="formRef"
            :options="getField('affected_by')['options']"
            @update:modelValue="handleAffectedByInput"
            @textarea-updated="formRef[$event.field] = $event.value"
        >
            <template v-slot:question>
                {{ getField("affected_by").question }}
            </template>
        </InputCheckboxGroup>
    </div>
</template>

<script setup>
import InputCheckboxGroup from "../../components/Programmes/GetInto/Form/InputCheckboxGroup.vue";
import InputRadioGroup from "../Form/InputRadioGroup";
import Form from "../../Form";
import { isRequired } from "./helpers";
import InputNumber from "../Form/InputNumber";
import { ref, markRaw } from "vue";

const props = defineProps({
    step: Object,
    fields: Object,
    formData: Object
})

let formRef = markRaw(ref(new Form({
    have_children: null,
    children_count: null,
    children_under_two: null,
    only_parent_carer: null,
    living_arrangements: null,
    living_arrangements_other: null,
    in_care_situation: null,
    in_care_type: null,
    how_satisfied_with_life: null,
    affected_by: [],
    affected_by_other_text: null
}, props.formData)))

const getField = (fieldName) => {
    return props.fields[fieldName];
}

const checkIsRequired = (fieldName) => {
    var fieldData = getField(fieldName);
    return isRequired(fieldData, formRef.value);
}

function  handleLivingArrangementInput(value) {
    formRef.value.errors.clear('living_arrangements');

    formRef.value.in_care_situation = ['Children\'s Home', 'Foster Care'].includes(value)
        ? 'Yes, I’m in care now'
        : null;
}

function clearCheckboxValues(previousAnswers, values, formField) { 
    for (const answer of previousAnswers) {
        if (values.includes(answer)) {
            formRef.value[formField] = values.filter(value => value !== answer);
        }
    }
}

function  handleAffectedByInput(values) {
    if (values.length && (values[values.length - 1] === 'affected_by_none' || values[values.length - 1] === 'prefer_not_to_say')) {
        formRef.value.affected_by = [values[values.length - 1]];
        return;
    }
    clearCheckboxValues(['affected_by_none', 'prefer_not_to_say'], values, 'affected_by');
}

defineExpose({
  formRef
})
</script>
